import { createContext, useContext, type PropsWithChildren } from "react";
import { ApolloProvider, type ApolloClient } from "@apollo/client";
import type { ShellHandleHost } from "./createShellHandle";
import type { ConnectivityApi } from "./connectivity";

const ShellHostContext = createContext<ShellHandleHost | null>(null);
const CloudClientContext = createContext<ApolloClient<unknown> | null>(null);
const ConnectivityContext = createContext<ConnectivityApi | null>(null);

export interface ShellHostProviderProps {
  host: ShellHandleHost;
  client: ApolloClient<unknown>;
  cloudClient?: ApolloClient<unknown>;
  connectivity?: ConnectivityApi;
}

export function ShellHostProvider({
  host,
  client,
  cloudClient,
  connectivity,
  children,
}: PropsWithChildren<ShellHostProviderProps>) {
  return (
    <ApolloProvider client={client}>
      <CloudClientContext.Provider value={cloudClient ?? null}>
        <ConnectivityContext.Provider value={connectivity ?? null}>
          <ShellHostContext.Provider value={host}>
            {children}
          </ShellHostContext.Provider>
        </ConnectivityContext.Provider>
      </CloudClientContext.Provider>
    </ApolloProvider>
  );
}

export function useCloudClient(): ApolloClient<unknown> | null {
  return useContext(CloudClientContext);
}

export function useConnectivity(): ConnectivityApi | null {
  return useContext(ConnectivityContext);
}

export function useShellHost(): ShellHandleHost {
  const v = useContext(ShellHostContext);
  if (!v)
    throw new Error("useShellHost must be used within <ShellHostProvider>");
  return v;
}
